import React, { useMemo, useState } from 'react';
import { Btn, DataTable, KPI, KPIRow, Loading, Msg, Section, Select } from '../kit';
import { RowClick } from '../search/StepTimings';
import {
  Dot, HEALTH_LEVEL, MODE_LABEL, PROBE_LEVEL, ProbeBadge, type RecrawlCtx, StatusPill, type UniRow, fmtAgo, fmtIST, n0, num,
  queueText, scheduleText, scrapeResults, str,
} from './shared';
import { RunAllModal, RunButton, RunStartModal } from './RunStart';

/**
 * Recrawl → Universities: every university the engine knows (one manifest
 * each, recrawl/manifests/<CODE>.json, merged with the admin's overrides),
 * with its kind, platform, schedule, latest dry / full run and its results,
 * latest probe (site reachability), next run, state and risk. The
 * headline numbers filter the table; a row opens the university drawer
 * (UniversityDrawer.tsx), where the schedule, throttle and flags are edited
 * and runs are started. Each row's Run ▸ opens the run dialog (RunStart.tsx)
 * straight away; "Queue all universities" queues one run each.
 */

const stateOf = (u: UniRow): string => {
  if (u.enabled === false) return 'disabled';
  if (u.needs_attention) return 'needs attention';
  if (u.paused) return 'paused';
  if (num(u.open_run_id) !== null) return 'open run';
  if (u.due) return 'due now';
  if (u.schedule?.enabled) return 'scheduled';
  return 'manual only';
};
const STATE_TONE: Record<string, 'red' | 'amber' | 'green' | 'grey' | 'blue' | 'navy'> = {
  disabled: 'grey', 'needs attention': 'red', paused: 'amber', 'open run': 'blue', 'due now': 'green',
  scheduled: 'navy', 'manual only': 'grey',
};

const FILTERS: Record<string, (u: UniRow) => boolean> = {
  scheduled: u => !!u.schedule?.enabled,
  due: u => !!u.due,
  attention: u => !!u.needs_attention || n0(u.consecutive_failures) >= 2,
  paused: u => !!u.paused || u.enabled === false,
  never: u => !u.last_scrape,
  manual: u => u.kind === 'manual_file',
};

const LABELS: Record<string, string> = {
  run: 'Run', code: 'Code', last_status: 'Last dry / full run', results: 'Results', last_at: 'Run at', probe: 'Latest probe',
  next_due_at: 'Next due', risk_n: 'Risk flags', active: 'Master active', live: 'Live rows', health: 'Requests',
  failures: 'Failures in a row',
};

const Universities: React.FC<{ ctx: RecrawlCtx; loading: boolean; error: string }> = ({ ctx, loading, error }) => {
  const [filter, setFilter] = useState('');
  const [kind, setKind] = useState('');
  const [platform, setPlatform] = useState('');
  const [runFor, setRunFor] = useState('');
  const [runAll, setRunAll] = useState(false);

  const platforms = useMemo(() => Array.from(new Set(ctx.universities.map(u => str(u.platform)).filter(Boolean))).sort(),
    [ctx.universities]);
  const rows = useMemo(() => ctx.universities
    .filter(u => (!filter || FILTERS[filter]?.(u)) && (!kind || u.kind === kind) && (!platform || u.platform === platform))
    .map(u => {
      const h = ctx.health.get(u.code);
      return {
        code: u.code, name: u.name, kind: u.kind === 'manual_file' ? 'manual file' : str(u.kind) || 'scraper',
        platform: str(u.platform), country: str(u.country), schedule: scheduleText(u.schedule),
        last_status: str(u.last_scrape?.status), last_mode: str(u.last_scrape?.mode),
        results: scrapeResults(u.last_scrape, true),
        last_at: u.last_scrape?.finished_at || u.last_scrape?.started_at || u.last_scrape?.created_at || null,
        probe: u.last_probe ? PROBE_LEVEL[u.last_probe.level]?.label || u.last_probe.level : '',
        next_due_at: u.schedule?.enabled ? u.next_due_at || null : null, state: stateOf(u),
        risk_n: u.risk.length, risk: u.risk.join(', '), active: n0(u.master.active), missing: n0(u.master.missing),
        removed: n0(u.master.removed), live: num(u.live_count), drift: num(u.drift),
        health: str(h?.recommendation?.level) || 'none', failures: n0(u.consecutive_failures),
        last_success_at: u.last_success_at || null, due_reason: str(u.due_reason), folder: str(u.folder),
        overridden: !!u.overridden, run: '', _u: u,
      };
    }), [ctx.universities, ctx.health, filter, kind, platform]);

  const count = (k: string) => ctx.universities.filter(FILTERS[k]).length;
  const kpi = (k: string, label: string, hint: string) => (
    <KPI label={label} value={count(k).toLocaleString()} hint={hint} active={filter === k}
      onClick={() => setFilter(f => (f === k ? '' : k))} />
  );

  if (loading) return <Loading label="Loading universities…" />;
  return (
    <>
      <KPIRow>
        <KPI label="Universities" value={ctx.universities.length.toLocaleString()} hint="click a card to filter"
          active={!filter} onClick={() => setFilter('')} />
        {kpi('scheduled', 'Scheduled', 'schedule switched on')}
        {kpi('due', 'Due now', 'the scheduler would start one')}
        {kpi('attention', 'Needs attention', 'flagged or failing')}
        {kpi('paused', 'Paused / disabled', 'no scheduled runs')}
        {kpi('never', 'Never crawled', 'no dry or full run yet')}
        {kpi('manual', 'Manual file', 'source uploaded by an admin')}
      </KPIRow>
      <Section title="Universities"
        right={
          <div className="flex items-center gap-2 flex-wrap">
            <Select value={kind} onChange={setKind} options={['', 'scraper', 'manual_file']}
              labels={{ '': 'All kinds', scraper: 'Crawled by a scraper', manual_file: 'Manual source file' }} />
            <Select value={platform} onChange={setPlatform} options={['', ...platforms]}
              labels={{ '': 'All platforms' }} />
            <Btn small onClick={() => setRunAll(true)}>Queue all universities…</Btn>
          </div>
        }>
        {error && <Msg kind="error">{error}</Msg>}
        <div className="text-xs text-grayscale-1100">
          Click a university for its manifest, throttle, schedule and runs; <b>Run ▸</b> starts a probe, dry run or full run (you confirm first).
          Times are India time (IST), like the schedules.
        </div>
        {runFor && <RunStartModal ctx={ctx} code={runFor} onClose={() => setRunFor('')} />}
        {runAll && <RunAllModal ctx={ctx} onClose={() => setRunAll(false)} />}
        <RowClick onRow={k => ctx.openUniversity(k)}>
          <DataTable rows={rows} labels={LABELS} csvName="recrawl_universities" maxHeight="40rem" tableId="recrawl_universities"
            columns={['run', 'code', 'name', 'kind', 'platform', 'schedule', 'last_status', 'results', 'last_at', 'probe', 'next_due_at',
              'state', 'risk_n', 'active', 'live', 'drift', 'health']}
            optionalColumns={['country', 'folder', 'missing', 'removed', 'failures', 'last_success_at', 'due_reason', 'risk', 'overridden']}
            empty={filter || kind || platform ? 'No university matches these filters.' : 'No universities (no manifests loaded).'}
            render={{
              run: r => <RunButton u={r._u} onRun={setRunFor} />,
              code: r => <b data-row-key={r.code} className="text-primary-700">{r.code}</b>,
              name: r => <span title={r.name}>{r.name}</span>,
              schedule: r => <span className={r._u.schedule?.enabled ? 'text-grayscale-1300' : 'text-grayscale-1100'}>{r.schedule}</span>,
              last_status: r => (r.last_status
                ? <span className="inline-flex items-center gap-1"><StatusPill status={r.last_status} /><span className="text-[11px] text-grayscale-1100">{MODE_LABEL[r.last_mode] || r.last_mode}{queueText(r._u.last_scrape) ? ` · ${queueText(r._u.last_scrape)}` : ''}</span></span>
                : <span className="text-grayscale-1100">never</span>),
              results: r => (r.results ? <span className="text-xs whitespace-nowrap" title={scrapeResults(r._u.last_scrape)}>{r.results}</span> : <span className="text-grayscale-1100">—</span>),
              probe: r => (r._u.last_probe
                ? <span className="text-xs whitespace-nowrap"><ProbeBadge probe={r._u.last_probe} /></span>
                : <span className="text-grayscale-1100">—</span>),
              last_at: r => <span title={fmtIST(r.last_at)}>{r.last_at ? fmtAgo(r.last_at) : '—'}</span>,
              next_due_at: r => <span title={r.due_reason}>{r.next_due_at ? fmtIST(r.next_due_at) : <span className="text-grayscale-1100">{r.due_reason || '—'}</span>}</span>,
              state: r => (
                <span className="inline-flex items-center gap-1" title={r.due_reason}>
                  <Dot tone={STATE_TONE[r.state] || 'grey'} /> {r.state}{r.overridden ? <span className="text-[10px] text-grayscale-1100"> · overrides</span> : null}
                </span>
              ),
              risk_n: r => (r.risk_n ? <span title={r.risk} className="text-amber-800">{r.risk_n} ⚑</span> : <span className="text-grayscale-1100">—</span>),
              active: r => <span>{r.active.toLocaleString()}</span>,
              live: r => <span>{r.live === null ? '—' : r.live.toLocaleString()}</span>,
              drift: r => {
                const listed = r.active + r.missing;
                const bad = r.drift !== null && listed > 0 && Math.abs(r.drift) > Math.max(5, 0.02 * listed);
                return <span className={bad ? 'text-red-700 font-medium' : ''}>{r.drift === null ? '—' : `${r.drift > 0 ? '+' : ''}${r.drift.toLocaleString()}`}</span>;
              },
              health: r => (
                <span className="inline-flex items-center gap-1" title={str(ctx.health.get(r.code)?.recommendation?.text)}>
                  <Dot tone={HEALTH_LEVEL[r.health]?.tone || 'grey'} /> {HEALTH_LEVEL[r.health]?.label || r.health}
                </span>
              ),
              last_success_at: r => <span>{fmtIST(r.last_success_at)}</span>,
            }} />
        </RowClick>
      </Section>
    </>
  );
};

export default Universities;
